import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { db, functions } from '../../../config/firebase';
import { collection, doc, getDoc, setDoc, updateDoc, deleteDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { PrimaryCamper, Dependant } from '../../../types/camper';
import PrimaryCamperForm from './PrimaryCamperForm';
import NotesSection from './NotesSection';
import OccupantsForm from './OccupantsForm';
import AccommodationHistory from './AccommodationHistory';
import './CamperManagementContainer.css';

interface CamperManagementContainerProps {
  primaryCamperId?: string;
  isEditing?: boolean;
  onSave?: () => void;
  selectedYear?: number | 'current';
}

const CamperManagementContainer: React.FC<CamperManagementContainerProps> = ({
  primaryCamperId,
  isEditing = false,
  onSave,
  selectedYear = 'current'
}) => {
  const { isAdmin, currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [primaryCamper, setPrimaryCamper] = useState<Partial<PrimaryCamper> | undefined>(undefined);
  const [archiveRegistration, setArchiveRegistration] = useState<any>(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [savedCamperId, setSavedCamperId] = useState<string | undefined>(undefined);
  
  const isReadOnly = selectedYear !== 'current';

  const loadCamperData = useCallback(async () => {
    if (!isAdmin) return;

    try {
      setLoading(true);
      const camperDoc = await getDoc(doc(db, 'primaryCampers', primaryCamperId!));
      if (camperDoc.exists()) {
        setPrimaryCamper(camperDoc.data() as PrimaryCamper);
      }
    } catch (err) {
      console.error('Error loading camper data:', err);
      setError('Failed to load camper data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, primaryCamperId]);

  const loadArchiveData = useCallback(async (year: number) => {
    if (!isAdmin || !primaryCamperId) return;

    try {
      setLoading(true);
      setArchiveRegistration(null);
      
      // First, still load the current camper data for basic info
      const camperDoc = await getDoc(doc(db, 'primaryCampers', primaryCamperId));
      if (camperDoc.exists()) {
        setPrimaryCamper(camperDoc.data() as PrimaryCamper);
      }

      // Load the archived registration for this camper from that year
      // Note: primaryCamperId is nested in registrationInfo
      const archiveRegQuery = query(
        collection(db, 'archive', String(year), 'registrations'),
        where('registrationInfo.primaryCamperId', '==', primaryCamperId)
      );
      
      const archiveSnapshot = await getDocs(archiveRegQuery);
      
      if (!archiveSnapshot.empty) {
        // Get the first (should be only) registration
        const regData = archiveSnapshot.docs[0].data();
        setArchiveRegistration({
          ...regData,
          id: archiveSnapshot.docs[0].id
        });
      } else {
        setArchiveRegistration(null);
      }
    } catch (err) {
      console.error('Error loading archive data:', err);
      setError(`Failed to load archive data for ${year}`);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, primaryCamperId]);

  useEffect(() => {
    if (!isAdmin) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }

    if (isEditing && primaryCamperId) {
      if (selectedYear === 'current') {
        loadCamperData();
      } else {
        loadArchiveData(selectedYear as number);
      }
    } else {
      setPrimaryCamper(undefined);
      setArchiveRegistration(null);
      setLoading(false);
    }
  }, [primaryCamperId, isAdmin, isEditing, selectedYear, loadCamperData, loadArchiveData]); 

  const createWalkinAccount = async (camperId: string, email: string) => {
    try {
      setCreatingAccount(true);
      setError(null);
      
      const createWalkinAccountFn = httpsCallable(functions, 'createWalkinAccount');
      
      const result = await createWalkinAccountFn({ 
        email: email,
        camperId: camperId 
      });
      
      console.log('Walk-in account created:', result.data);
      setAccountCreated(true);
      setSavedCamperId(camperId);
      
      // Reload camper data to show updated loginExists status
      if (isEditing && primaryCamperId) {
        await loadCamperData();
      }
      
      return true;
    } catch (err: any) {
      console.error('Error creating walk-in account:', err);
      setError(`Failed to create account: ${err.message || 'Unknown error'}`);
      return false;
    } finally {
      setCreatingAccount(false);
    }
  };

  const handlePrimaryCamperSubmit = async (data: Partial<PrimaryCamper>) => {
    try {
      console.log('Starting handlePrimaryCamperSubmit with data:', JSON.stringify(data));
      console.log('isEditing:', isEditing, 'primaryCamperId:', primaryCamperId);
      
      let camperId = primaryCamperId;
      
      if (isEditing && primaryCamperId) {
        console.log('Updating existing camper with ID:', primaryCamperId);
        await updateDoc(doc(db, 'primaryCampers', primaryCamperId), {
          ...data,
          lastModifiedDate: new Date()
        });
        console.log('Successfully updated existing camper');
      } else {
        console.log('Creating new camper...');
        const newCamperRef = doc(collection(db, 'primaryCampers'));
        camperId = newCamperRef.id;
        console.log('Generated new document reference with ID:', camperId);
        
        const camperData = {
          ...data,
          recordId: camperId,
          dependants: [],
          notes: [],
          deleted: false, // Explicitly setting deleted to false
          createdDate: new Date(),
          lastModifiedDate: new Date()
        };
        
        console.log('Preparing to save data:', JSON.stringify(camperData));
        await setDoc(newCamperRef, camperData);
        console.log('Successfully saved new camper with ID:', camperId);
      }
      
      // After successful save, create auth account if email is provided
      if (data.email && camperId) {
        const accountSuccess = await createWalkinAccount(camperId, data.email);
        if (!accountSuccess) {
          // Show error but don't fail the whole save
          console.error('Camper saved but account creation failed');
        }
      } else {
        setError('No email provided - cannot create auth account');
      }
      
      console.log('handlePrimaryCamperSubmit completed successfully');
    } catch (err) {
      console.error('Error saving primary camper:', err);
      // Log additional details about the error
      if (err instanceof Error) {
        console.error('Error name:', err.name);
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
      }
      
      // Display visible error to user
      setError(`Failed to save camper: ${err instanceof Error ? err.message : 'Unknown error'}`);
      
      // Alert the error for immediate visibility
      alert(`Failed to save camper: ${err instanceof Error ? err.message : 'Unknown error'}`);
      
      throw new Error('Failed to save primary camper');
    }
  };

  const handleDependantsSubmit = async (dependants: Partial<Dependant>[]) => {
    if (!primaryCamperId) return;

    try {
      await updateDoc(doc(db, 'primaryCampers', primaryCamperId), {
        dependants,
        lastModifiedDate: new Date()
      });
      loadCamperData();
    } catch (err) {
      console.error('Error saving dependants:', err);
      setError('Failed to save dependants');
    }
  };

  const handleDelete = async () => {
    if (!primaryCamperId || !isEditing) return;

    if (window.confirm('Are you sure you want to delete this camper? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'primaryCampers', primaryCamperId));
        if (onSave) {
          onSave();
        }
      } catch (err) {
        console.error('Error deleting camper:', err);
        setError('Failed to delete camper');
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading camper details...</div>;
  }

  if (!isAdmin) {
    return <div className="error-message">Access denied. Admin privileges required.</div>;
  }

  const handleRegisterCamper = () => {
    if (savedCamperId) {
      // Navigate to registration flow - needs implementation for admin-on-behalf flow
      navigate(`/register/wizard?camperId=${savedCamperId}&adminMode=true`);
    }
  };

  const handleBackToCamperList = () => {
    if (onSave) {
      onSave();
    } else {
      navigate('/admin/campers');
    }
  };

  return (
    <div className="camper-management-container">
      {error && <div className="error-message">{error}</div>}
      
      {accountCreated && (
        <div className="success-message" style={{ 
          padding: '1.5rem', 
          margin: '1rem 0', 
          backgroundColor: '#d4edda', 
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          gridColumn: '1 / -1'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#155724' }}>✓ Account Created Successfully</h3>
          <p style={{ margin: '0 0 1rem 0', color: '#155724' }}>
            The camper account has been created and linked. You can now register this camper or return to the camper list.
          </p>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              onClick={handleRegisterCamper}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Register This Camper
            </button>
            <button 
              onClick={handleBackToCamperList}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Back to Camper List
            </button>
          </div>
        </div>
      )}
      
      {creatingAccount && (
        <div style={{ 
          padding: '1rem', 
          margin: '1rem 0', 
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          gridColumn: '1 / -1'
        }}>
          Creating authentication account...
        </div>
      )}
      
      {isReadOnly && archiveRegistration && (
        <div className="form-section" style={{ gridColumn: '1 / -1', backgroundColor: '#f8f9fa' }}>
          <h2>Registration Information for {selectedYear}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
            <div>
              <strong>Invoice Number:</strong> {archiveRegistration.invoiceNumber || 'N/A'}
            </div>
            <div>
              <strong>Accommodation:</strong> {archiveRegistration.accommodation?.firstChoice || archiveRegistration.financial?.accommodationFees?.placeName || 'N/A'}
            </div>
            <div>
              <strong>Total Amount:</strong> ${(archiveRegistration.financial?.totalAmount || 0).toFixed(2)}
            </div>
            <div>
              <strong>Amount Paid:</strong> ${(() => {
                const payments = archiveRegistration.paymentHistory || [];
                const totalPaid = payments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
                return totalPaid.toFixed(2);
              })()}
            </div>
            <div>
              <strong>Balance:</strong> ${(() => {
                const total = archiveRegistration.financial?.totalAmount || 0;
                const payments = archiveRegistration.paymentHistory || [];
                const totalPaid = payments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
                return (total - totalPaid).toFixed(2);
              })()}
            </div>
            <div>
              <strong>Status:</strong> {archiveRegistration.status || 'N/A'}
            </div>
          </div>
          {archiveRegistration.attendingCampers && archiveRegistration.attendingCampers.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3>Occupants ({archiveRegistration.attendingCampers.length})</h3>
              <ul style={{ marginTop: '0.5rem' }}>
                {archiveRegistration.attendingCampers.map((occ: any, idx: number) => (
                  <li key={idx}>
                    {occ.firstName} {occ.lastName} ({occ.category || occ.age})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {archiveRegistration.campDates && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3>Stay Details</h3>
              <p>
                <strong>Arrival:</strong> {new Date(archiveRegistration.campDates.arrivalDate?.toDate?.() || archiveRegistration.campDates.arrivalDate).toLocaleDateString()} 
                {' → '}
                <strong>Departure:</strong> {new Date(archiveRegistration.campDates.departureDate?.toDate?.() || archiveRegistration.campDates.departureDate).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      )}

      {isReadOnly && !archiveRegistration && !loading && (
        <div className="form-section" style={{ gridColumn: '1 / -1', backgroundColor: '#f8f9fa', textAlign: 'center' }}>
          <p style={{ fontSize: '1.1em', color: '#6c757d' }}>
            No registration found for this camper in {selectedYear}
          </p>
        </div>
      )}
      
      <div className="form-section primary-camper-section">
        {isEditing && !isReadOnly && (
          <button 
            className="delete-button" 
            onClick={handleDelete}
            aria-label="Delete Camper"
          >
            Delete Camper
          </button>
        )}
        <PrimaryCamperForm
          onSubmit={handlePrimaryCamperSubmit}
          initialData={primaryCamper}
          isEditing={isEditing && !isReadOnly}
        />
      </div>

      {isEditing && primaryCamperId && primaryCamper && (
        <>
          <div className="form-section occupants-section">
            <OccupantsForm
              primaryCamperId={primaryCamperId}
              primaryCamper={primaryCamper}
              initialOccupants={primaryCamper.dependants || []}
              onSubmit={handleDependantsSubmit}
              isReadOnly={isReadOnly}
            />
          </div>

          <div className="form-section notes-section">
            <NotesSection
              camperId={primaryCamperId}
              notes={primaryCamper.notes || []}
              onNotesChange={loadCamperData}
              isReadOnly={isReadOnly}
            />
          </div>

          <div className="form-section history-section">
            <AccommodationHistory 
              primaryCamperId={primaryCamperId}
              isReadOnly={isReadOnly}
            />
          </div>
        </>
      )}

    </div>
  );
};

export default CamperManagementContainer; 